import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { formatDistanceToNow } from "date-fns";

export interface Notification {
  id: string;
  title?: string;
  message?: string;
  timestamp?: string;
  is_read?: boolean;
  timeAgo?: string;
  isNew?: boolean;
  type?: string;
  data?: unknown;
  app_name?: string;
  object_id?: string;
  related_id?: string;
  from_user?: {
    full_name?: string;
    employee_id?: string;
  };
}

interface NotificationContextType {
  notifications: Notification[];
  setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>;
  unreadCount: number;
  connectionStatus: "disconnected" | "connecting" | "connected" | "error" | "authentication_failed";
  error: string | null;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  refreshNotifications: () => void;
  reconnect: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

const STORAGE_KEY = "discipline_notifications";

const loadStoredNotifications = (): Notification[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error("[NotificationContext] Error loading stored notifications:", e);
  }
  return [];
};

const saveNotificationsToStorage = (notifications: Notification[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications));
  } catch (e) {
    console.error("[NotificationContext] Error saving notifications:", e);
  }
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>(() => loadStoredNotifications());
  const [unreadCount, setUnreadCount] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState<NotificationContextType["connectionStatus"]>("disconnected");
  const [error, setError] = useState<string | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(false);
  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_DELAY = 5000;

  const getToken = useCallback(() => {
    const token = sessionStorage.getItem("accessToken") || 
                  sessionStorage.getItem("authToken") || 
                  sessionStorage.getItem("token");
    return token;
  }, []);

  const getWebSocketUrl = useCallback(() => {
    const baseUrl = process.env.REACT_APP_WS_URL;
    if (!baseUrl) {
      console.error("[NotificationContext] REACT_APP_WS_URL not configured");
      return null;
    }
    const token = getToken();
    const url = token 
      ? `${baseUrl}?token=${encodeURIComponent(token)}`
      : baseUrl;
    return url;
  }, [getToken]);

  const updateUnreadCount = useCallback((notifs: Notification[]) => {
    const count = notifs.filter((n) => !n.is_read).length;
    setUnreadCount(count);
  }, []);

  useEffect(() => {
    updateUnreadCount(notifications);
  }, [notifications, updateUnreadCount]);

  useEffect(() => {
    saveNotificationsToStorage(notifications);
  }, [notifications]);

  const handleNewNotifications = useCallback(
    (data: Notification | Notification[]) => {
      if (!isMountedRef.current) return;
      
      const rawNotifications = Array.isArray(data) ? data : [data];
      
      // The service sends type "<app>.<Model>" (no app_name): keep discipline's.
      const appOf = (n: any) => n.app_name || String(n.type || "").split(".")[0];
      const filteredNotifications = rawNotifications.filter(
        (n: any) => n && n.id && (appOf(n) === "Prosecutor_Dicipline_Service" || (!n.app_name && !n.type)),
      );
      
      if (filteredNotifications.length === 0) {
        console.log("[NotificationContext] No discipline notifications in data");
        return;
      }
      
      const formattedNotifications = filteredNotifications.map((notification: any) => {
        // The service sends created_at; the record type is in "type".
        const timestamp = notification.timestamp || notification.created_at;
        const date = timestamp ? new Date(timestamp) : null;
        return {
          ...notification,
          timestamp,
          model_name: notification.model_name || String(notification.type || "").split(".").pop(),
          timeAgo: date && !Number.isNaN(date.getTime()) ? formatDistanceToNow(date) : undefined,
          isNew: !notification.is_read,
        };
      });

      // New ones first; ones already listed take the server's state (read...).
      setNotifications((prev) => {
        const incoming = new Map(formattedNotifications.map((n: any) => [n.id, n]));
        const updated = prev.map((n) => (incoming.has(n.id) ? { ...n, ...incoming.get(n.id) } : n));
        const fresh = formattedNotifications.filter((n: any) => !prev.some((p) => p.id === n.id));
        return [...fresh, ...updated];
      });
    },
    [updateUnreadCount],
  );

  const cleanupWebSocket = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    if (socketRef.current) {
      socketRef.current.onopen = null;
      socketRef.current.onmessage = null;
      socketRef.current.onerror = null;
      socketRef.current.onclose = null;

      if (socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.close();
      }

      socketRef.current = null;
    }
  }, []);

  const handleAuthenticationFailure = useCallback(() => {
    console.error("[NotificationContext] Authentication failed");
    setConnectionStatus("authentication_failed");
    setError("Authentication failed. Please log in again.");
    sessionStorage.removeItem("accessToken");
    sessionStorage.removeItem("token");
    localStorage.removeItem("access_token");
  }, []);

  const connectWebSocket = useCallback(() => {
    if (
      !isMountedRef.current ||
      reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS
    ) {
      return;
    }

    cleanupWebSocket();
    setConnectionStatus("connecting");

    const wsUrl = getWebSocketUrl();

    if (!wsUrl) {
      console.error("[NotificationContext] Cannot connect: No WebSocket URL");
      setConnectionStatus("error");
      return;
    }

    socketRef.current = new WebSocket(wsUrl);

    socketRef.current.onopen = () => {
      if (!isMountedRef.current) return;

      console.log("[NotificationContext] WebSocket connected");
      reconnectAttemptsRef.current = 0;
      setConnectionStatus("connected");
      setError(null);

      // Always ask for the list, so the cached one is brought up to date.
      socketRef.current?.send(JSON.stringify({ type: "request_notifications" }));
    };

    socketRef.current.onmessage = (event) => {
      if (!isMountedRef.current) return;

      console.log("[NotificationContext] Raw message received:", event.data);

      try {
        const message = JSON.parse(event.data);
        console.log("[NotificationContext] Parsed message:", message);

        switch (message.type) {
          case "notifications":
            handleNewNotifications(message.data);
            break;
          case "notification":
            handleNewNotifications(message.data);
            break;
          case "new_notification":
            handleNewNotifications(message.data);
            break;
case "read_confirmation":
              if (message.success) {
                const targetId = message.id || message.notification_id;
                if (targetId) {
                  setNotifications((prev) =>
                    prev.map((n) =>
                      n.id === targetId ? { ...n, is_read: true } : n
                    )
                  );
                }
              }
              break;
          case "initial_count":
            setUnreadCount(message.count);
            break;
          case "unread_count":
            setUnreadCount(message.count);
            break;
          case "heartbeat":
            if (socketRef.current?.readyState === WebSocket.OPEN) {
              socketRef.current.send(
                JSON.stringify({ type: "heartbeat", message: "pong" }),
              );
            }
            break;
          case "error":
            setError(message.message);
            console.error(
              "[NotificationContext] WebSocket error:",
              message.message,
            );
            break;
          default:
            console.warn(
              "[NotificationContext] Unknown message type:",
              message.type,
            );
            break;
        }
      } catch (err) {
        console.error("[NotificationContext] Error processing message:", err);
      }
    };

    socketRef.current.onerror = (error) => {
      if (!isMountedRef.current) return;
      setConnectionStatus("error");
      console.error("[NotificationContext] WebSocket error:", error);
    };

    socketRef.current.onclose = (event) => {
      if (!isMountedRef.current) return;

      if (event.code === 4001) {
        handleAuthenticationFailure();
        return;
      }

      if (!event.wasClean) {
        setConnectionStatus("disconnected");
        reconnectAttemptsRef.current += 1;
        const delay = Math.min(
          RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current),
          60000,
        );
        console.warn(
          "[NotificationContext] WebSocket closed unexpectedly. Reconnecting in",
          delay,
          "ms",
        );
        setTimeout(connectWebSocket, delay);
      } else {
        console.log("[NotificationContext] WebSocket closed cleanly");
      }
    };
  }, [
    cleanupWebSocket,
    getWebSocketUrl,
    handleNewNotifications,
    handleAuthenticationFailure,
  ]);

  const markAsRead = useCallback((id: string) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(
        JSON.stringify({
          type: "mark_as_read",
          notification_id: id,
        }),
      );
    }
  }, []);

  const markAllAsRead = useCallback(() => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(
        JSON.stringify({
          type: "mark_all_read",
        }),
      );
    }
  }, []);

  const refreshNotifications = useCallback(() => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({ type: "request_notifications" }));
    }
  }, []);

  const reconnect = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    connectWebSocket();
  }, [connectWebSocket]);

  useEffect(() => {
    isMountedRef.current = true;
    connectWebSocket();

    return () => {
      isMountedRef.current = false;
      cleanupWebSocket();
    };
  }, [connectWebSocket]);

  const value: NotificationContextType = {
    notifications,
    setNotifications,
    unreadCount,
    connectionStatus,
    error,
    markAsRead,
    markAllAsRead,
    refreshNotifications,
    reconnect,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider",
    );
  }
  return context;
};